import { createFileRoute } from '@tanstack/react-router';
import { useTranslate } from '@tolgee/react';
import { AppRoute } from '@agentselly/immodossier-shared';
import { useLoaderQuery } from '~/helper/useLoaderQuery';
import { useAppParams } from '~/helper/useAppParams';
import { LoadingWidget } from '~/components/widget/base/LoadingWidget';
import { ErrorWidget } from '~/components/widget/base/ErrorWidget';
import { RenderWidgets } from '~/components/widget/RenderWidgets';
import { cockpitOffersWidgets } from '~/config/widgets/cockpit/cockpitOffersWidgets';
import { useAuthz } from '~/context/AuthzContext';
import {
  fmtChf,
  fmtDate,
  Kpi,
  LineChart,
  PageHead,
  Privacy,
  V3Page,
} from '~/components/cockpit/v3';
import {
  diffToAsking,
  type WidgetProps,
} from '~/components/cockpit/cockpitData';

export const Route = createFileRoute(
  AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_OFFERS,
)({
  component: OffersPage,
});

interface Offer {
  readonly date: string;
  readonly offerPrice: string;
  readonly person: string;
  readonly personEmail?: string;
  readonly personPhone?: string;
  readonly message?: string;
  readonly offerConfirmationSecureAssetsPath?: string;
}

function OffersPage(): React.ReactElement {
  const params = useAppParams(Route);
  const { t } = useTranslate();
  const { atLeast } = useAuthz();
  const isAdvisor = atLeast('partner');

  const query = useLoaderQuery(
    AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_OFFERS,
    { params },
  );

  if (query.isLoading) return <LoadingWidget />;
  if (query.isError || !query.data) return <ErrorWidget error={query.error} />;

  if (!query.data.v3Redesign) {
    const widgetProps = query.data?.widgetProps || {};
    const widgets = cockpitOffersWidgets(widgetProps);
    return (
      <div className="h-full flex flex-col gap-y-6">
        <RenderWidgets widgets={widgets} />
      </div>
    );
  }

  const wp = (query.data.widgetProps || {}) as WidgetProps;
  const offers: Offer[] = [
    ...(wp.cockpitOffersInDetailWidget?.data ?? []),
  ].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  const secureUrl: string =
    wp.cockpitOffersInDetailWidget?.secureAssetsUrl ?? '';
  const asking = Number(wp.cockpitOffersDetailWidget?.price) || 0;
  const prices = offers.map((o) => Number(o.offerPrice) || 0);
  const best = Math.max(0, ...prices);
  const bestDiff = diffToAsking(best, asking);

  return (
    <V3Page>
      <PageHead
        title={t('cockpit_offers_title', 'Angebote')}
        sub={t(
          'cockpit_offers_sub',
          'Eingegangene Kaufangebote im Vergleich zum Angebotspreis.',
        )}
      />
      <div
        className="grid g3"
        data-widget="cockpitOffersTrendWidget"
      >
        <Kpi
          value={offers.length}
          label={t('cockpit_offers_kpi_count', 'Angebote')}
        />
        <Kpi
          value={fmtChf(best)}
          label={t('cockpit_offers_kpi_best', 'Höchstes Angebot')}
          delta={null}
        />
        <Kpi
          value={fmtChf(asking)}
          label={t('cockpit_offers_kpi_asking', 'Angebotspreis')}
        />
      </div>
      <div className="cols">
        <section
          className="card"
          data-widget="cockpitOffersInDetailWidget"
        >
          <div className="sec">
            <h2>{t('cockpit_offers_list', 'Eingegangene Angebote')}</h2>
          </div>
          {offers.length === 0 ? (
            <p className="mu">
              {t('cockpit_offers_empty', 'Noch keine Angebote eingegangen.')}
            </p>
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>{t('cockpit_offers_col_date', 'Datum')}</th>
                  <th>{t('cockpit_offers_col_person', 'Interessent')}</th>
                  <th className="r">
                    {t('cockpit_offers_col_price', 'Angebot')}
                  </th>
                  <th className="r">
                    {t('cockpit_offers_col_diff', 'Zum Angebotspreis')}
                  </th>
                  {isAdvisor ? (
                    <th>{t('cockpit_offers_col_contact', 'Kontakt')}</th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {[...offers].reverse().map((o, i) => {
                  const d = diffToAsking(Number(o.offerPrice), asking);
                  return (
                    <tr key={`${o.date}-${i}`}>
                      <td data-l={t('cockpit_offers_col_date', 'Datum')}>
                        {fmtDate(o.date)}
                      </td>
                      <td
                        data-l={t('cockpit_offers_col_person', 'Interessent')}
                      >
                        {o.person}
                      </td>
                      <td
                        className="r"
                        data-l={t('cockpit_offers_col_price', 'Angebot')}
                      >
                        {fmtChf(o.offerPrice)}
                      </td>
                      <td
                        className="r"
                        data-l={t(
                          'cockpit_offers_col_diff',
                          'Zum Angebotspreis',
                        )}
                      >
                        {d ? (
                          <span
                            className={`chip ${d.pct >= 0 ? 'c-ok' : 'c-warn'}`}
                          >
                            {d.label}
                          </span>
                        ) : (
                          '–'
                        )}
                      </td>
                      {isAdvisor ? (
                        <td data-l={t('cockpit_offers_col_contact', 'Kontakt')}>
                          <span>
                            {o.personEmail ? (
                              <a
                                className="lnk"
                                href={`mailto:${o.personEmail}`}
                              >
                                {o.personEmail}
                              </a>
                            ) : null}
                            {o.personPhone ? (
                              <a
                                className="lnk"
                                href={`tel:${o.personPhone}`}
                              >
                                {o.personPhone}
                              </a>
                            ) : null}
                            {o.offerConfirmationSecureAssetsPath ? (
                              <a
                                className="lnk"
                                href={`${secureUrl}${o.offerConfirmationSecureAssetsPath}`}
                                target="_blank"
                                rel="noreferrer"
                              >
                                {t(
                                  'cockpit_offers_confirmation',
                                  'Bestätigung',
                                )}
                              </a>
                            ) : null}
                          </span>
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {isAdvisor ? null : (
            <Privacy>
              {t(
                'cockpit_offers_privacy',
                'Kontaktdaten und Nachrichten von Interessenten sehen nur Ihr Berater und Ihr Team.',
              )}
            </Privacy>
          )}
        </section>
        <aside className="side">
          <section className="card">
            <div className="sec">
              <h2>
                {t('cockpit_offers_compare', 'Vergleich zum Angebotspreis')}
              </h2>
            </div>
            {bestDiff ? (
              <p>
                {t('cockpit_offers_best_is', 'Das höchste Angebot liegt')}{' '}
                <strong>{bestDiff.label}</strong>{' '}
                {t('cockpit_offers_vs_asking', 'gegenüber dem Angebotspreis.')}
              </p>
            ) : (
              <p className="mu">
                {t('cockpit_offers_no_compare', 'Noch kein Vergleich möglich.')}
              </p>
            )}
          </section>
          {prices.length > 1 ? (
            <section className="card">
              <div className="sec">
                <h2>{t('cockpit_offers_trend', 'Verlauf')}</h2>
              </div>
              <LineChart
                title={t('cockpit_offers_trend', 'Verlauf')}
                values={prices}
                labels={[
                  fmtDate(offers[0].date),
                  fmtDate(offers[offers.length - 1].date),
                ]}
              />
            </section>
          ) : null}
        </aside>
      </div>
    </V3Page>
  );
}
